import Header from "@/components/header/Header.vue";
import Footer from "@/components/footer/Footer.vue";
import BookCard from "@/components/book-card/BookCard.vue";
import { useToast } from "vue-toastification";
import axios from "@/config/axios";
import { updateTrendingCacheIfBookExists } from '@/utils/trendingCache';

export default {
  name: "ReadingProgress",
  components: {
    Header,
    Footer,
    BookCard
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      bookNotes: [],
      activeTab: "all",
      searchKeyword: "",
      sortBy: "latest", // 'latest', 'progress', 'title', 'rating'
      viewMode: "grid", // 'grid' or 'list'
      defaultCover: "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?auto=format&fit=crop&w=400&q=80",
      
      // Mouse Drag-to-Scroll State
      isDraggingTabs: false,
      dragStartX: 0,
      dragScrollLeft: 0,
      hasDraggedFar: false,

      showSortDropdown: false,
      showMobileFilters: false,
      showFilterModal: false,
      showNoteViewModal: false,
      viewNoteItem: null,
      deleteConfirmModal: {
        show: false,
        bookId: null,
        bookName: null,
        isDeleting: false
      },
      sortOptions: [
        { value: "latest", label: "Mới cập nhật", icon: "fa-solid fa-clock-rotate-left" },
        { value: "progress", label: "Tiến độ cao nhất", icon: "fa-solid fa-chart-line" },
        { value: "title", label: "Tên sách A - Z", icon: "fa-solid fa-arrow-down-a-z" },
        { value: "rating", label: "Đánh giá cao nhất", icon: "fa-solid fa-star" }
      ],

      ownFilter: "all",
      showOwnDropdown: false,
      ownFilterOptions: [
        { value: "all", label: "Tất cả sở hữu", icon: "fa-solid fa-boxes-stacked" },
        { value: "1", label: "Sách giấy", icon: "fa-solid fa-book" },
        { value: "2", label: "Ebook", icon: "fa-solid fa-tablet-screen-button" },
        { value: "0", label: "Chưa sở hữu", icon: "fa-solid fa-box-open" }
      ],

      selectedStatuses: ["all"],
      selectedOwnerships: ["all"],

      progressStatuses: [
        { value: 0, label: "Chưa đọc", icon: "fa-solid fa-xmark" },
        { value: 1, label: "Muốn đọc", icon: "fa-solid fa-bookmark" },
        { value: 2, label: "Đang đọc", icon: "fa-solid fa-book-open" },
        { value: 3, label: "Đã đọc xong", icon: "fa-solid fa-circle-check" },
        { value: 4, label: "Tạm ngưng", icon: "fa-solid fa-circle-pause" }
      ],

      ownStatuses: [
        { value: 1, label: "Sách giấy", icon: "fa-solid fa-book" },
        { value: 2, label: "Ebook", icon: "fa-solid fa-tablet-screen-button" },
        { value: 0, label: "Chưa sở hữu", icon: "fa-solid fa-xmark" }
      ],

      // Modal state
      showModal: false,
      activeEditItem: null,
      modalForm: {
        bookId: "",
        progressStatus: 2,
        type: false, // false: pages, true: percentage
        currentPage: 0,
        totalPage: 0,
        percent: 0,
        note: "",
        ownStatus: 0
      },
      showNoteTextarea: false,
      collapseReadingStatus: false,
      collapseOwnership: false,
      isMobile: false,
      originalBookNote: null,
      expandedNotes: {},

      // Pagination State
      currentPage: 0,
      totalPages: 0,
      totalElements: 0,
      stats: null,
      searchTimeout: null,
      isControlBarStuck: false
    };
  },
  computed: {
    isNoteModified() {
      if (!this.originalBookNote) return false;
      const m = this.modalForm;
      const o = this.originalBookNote;
      return (
        Number(m.progressStatus) !== Number(o.progressStatus) ||
        Number(m.ownStatus) !== Number(o.ownStatus) ||
        m.type !== o.type ||
        Number(m.currentPage) !== Number(o.currentPage) ||
        parseFloat(m.percent || 0) !== parseFloat(o.percent || 0) ||
        (m.note || '').trim() !== (o.note || '').trim()
      );
    },
    hadPreviousBookData() {
      if (!this.originalBookNote) return false;
      return Number(this.originalBookNote.progressStatus) !== 0 || Number(this.originalBookNote.ownStatus) !== 0;
    },
    isDeleteAction() {
      return Number(this.modalForm.progressStatus) === 0 && Number(this.modalForm.ownStatus) === 0 && this.hadPreviousBookData;
    },
    totalCount() {
      return this.stats?.total || 0;
    },
    hasExistingNote() {
      const note = (this.originalBookNote?.note || this.modalForm?.note || '').trim();
      return !!note && note !== '.' && note !== '-' && !note.startsWith('Cập nhật trạng thái: ');
    },
    visualPercent() {
      const p = parseFloat(this.modalForm.percent);
      if (!isNaN(p)) {
        return Math.min(100, Math.max(0, Math.round(p * 10) / 10));
      }
      if (this.modalForm.totalPage > 0) {
        const pct = (this.modalForm.currentPage / this.modalForm.totalPage) * 100;
        return Math.min(100, Math.max(0, Math.round(pct * 10) / 10));
      }
      return 0;
    },
    noteValidationError() {
      if (this.modalForm.note && this.modalForm.note.length > 500) {
        return 'Ghi chú không được vượt quá 500 ký tự.';
      }
      if (Number(this.modalForm.progressStatus) === 2 || Number(this.modalForm.progressStatus) === 4) {
        if (!this.modalForm.type) {
          const curr = Number(this.modalForm.currentPage) || 0;
          const total = Number(this.modalForm.totalPage) || 0;
          if (curr < 0 || total < 0) {
            return 'Số trang không được âm.';
          }
          if (curr > total) {
            return 'Số trang hiện tại không thể lớn hơn tổng số trang.';
          }
        } else {
          const pct = parseFloat(this.modalForm.percent);
          if (isNaN(pct) || pct < 0 || pct > 100) {
            return 'Tỉ lệ % tiến độ phải từ 0 đến 100.';
          }
        }
      }
      return '';
    },
    unreadCount() {
      return this.stats?.unread || 0;
    },
    wantCount() {
      return this.stats?.want || 0;
    },
    readingCount() {
      return this.stats?.reading || 0;
    },
    completedCount() {
      return this.stats?.completed || 0;
    },
    pausedCount() {
      return this.stats?.paused || 0;
    },
    overallPercent() {
      if (!this.bookNotes || this.bookNotes.length === 0) return 0;
      const totalPct = this.bookNotes.reduce((sum, item) => sum + this.calculateVisualPercent(item), 0);
      return Math.round(totalPct / this.bookNotes.length);
    },
    activeFilterCount() {
      let count = 0;
      if (!this.selectedStatuses.includes("all")) {
        count += this.selectedStatuses.length;
      }
      if (!this.selectedOwnerships.includes("all")) {
        count += this.selectedOwnerships.length;
      }
      return count;
    },
    filteredBookNotes() {
      return this.bookNotes;
    },
    visiblePages() {
      const current = this.currentPage;
      const total = this.totalPages;
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i);
      }

      const pages = [];
      pages.push(0);

      const start = Math.max(1, current - 1);
      const end = Math.min(total - 2, current + 1);

      if (start > 1) {
        pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < total - 2) {
        pages.push('...');
      }

      pages.push(total - 1);

      return pages;
    }
  },
  watch: {
    searchKeyword() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchUserBookNotes(0, true);
      }, 800);
    }
  },
  beforeRouteEnter(to, from, next) {
    const isFromBookDetail = from && (from.name === 'BookDetail' || (from.path && from.path.startsWith('/book/')));
    if (!isFromBookDetail) {
      sessionStorage.removeItem('bookcase_state');
    }
    next(vm => {
      if (!isFromBookDetail) {
        vm.resetToDefaultState();
      }
    });
  },
  beforeRouteLeave(to, from) {
    if (to && (to.name === 'BookDetail' || (to.path && to.path.startsWith('/book/')))) {
      this.saveStateToStorage();
    } else {
      sessionStorage.removeItem('bookcase_state');
    }
  },
  mounted() {
    this.checkMobile();
    window.addEventListener("resize", this.checkMobile);
    window.addEventListener("scroll", this.handleControlBarScroll, { passive: true });
    this.restoreStateFromStorage();
    this.fetchUserBookNotes(this.currentPage || 0);
    document.addEventListener("click", this.handleClickOutside);
    this.$nextTick(() => {
      this.handleControlBarScroll();
    });
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkMobile);
    window.removeEventListener("scroll", this.handleControlBarScroll);
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    resetToDefaultState() {
      this.searchKeyword = '';
      this.selectedStatuses = ['all'];
      this.selectedOwnerships = ['all'];
      this.sortBy = 'latest';
      this.currentPage = 0;
      this.viewMode = 'grid';
    },
    saveStateToStorage() {
      try {
        const state = {
          searchKeyword: this.searchKeyword || '',
          selectedStatuses: this.selectedStatuses || ['all'],
          selectedOwnerships: this.selectedOwnerships || ['all'],
          sortBy: this.sortBy || 'latest',
          viewMode: this.viewMode || 'grid',
          currentPage: this.currentPage || 0
        };
        sessionStorage.setItem('bookcase_state', JSON.stringify(state));
      } catch (e) {
        console.error('Error saving bookcase_state', e);
      }
    },
    restoreStateFromStorage() {
      try {
        const savedStr = sessionStorage.getItem('bookcase_state');
        if (savedStr) {
          const saved = JSON.parse(savedStr);
          if (typeof saved.searchKeyword === 'string') this.searchKeyword = saved.searchKeyword;
          if (Array.isArray(saved.selectedStatuses) && saved.selectedStatuses.length > 0) this.selectedStatuses = saved.selectedStatuses;
          if (Array.isArray(saved.selectedOwnerships) && saved.selectedOwnerships.length > 0) this.selectedOwnerships = saved.selectedOwnerships;
          if (saved.sortBy) this.sortBy = saved.sortBy;
          if (saved.viewMode) this.viewMode = saved.viewMode;
          if (typeof saved.currentPage === 'number' && saved.currentPage >= 0) this.currentPage = saved.currentPage;
        }
      } catch (e) {
        console.error('Error restoring bookcase_state', e);
      }
    },
    goSearch(text) {
      const key = (text || '').trim();
      if (!key) return;
      window.currentSearchKey = key;
      sessionStorage.setItem('lastSearchKey', key);
      sessionStorage.setItem('lastSearchPage', '0');
      const event = new CustomEvent('search-key-sync', { detail: key });
      window.dispatchEvent(event);
      this.$router.push({
        path: '/search',
        state: { key, page: 0 }
      }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error(err);
        }
      });
    },
    getAuthorsList(item) {
      if (!item) return [];
      const list = [];
      if (Array.isArray(item.authors) && item.authors.length > 0) {
        item.authors.forEach(a => {
          if (typeof a === 'object' && a !== null) {
            const name = a.name || a.authorName || '';
            if (name) list.push({ id: a.id || null, name });
          } else if (typeof a === 'string' && a.trim()) {
            list.push({ id: null, name: a.trim() });
          }
        });
      } else if (item.authorsStr && typeof item.authorsStr === 'string' && item.authorsStr.trim()) {
        const raw = item.authorsStr.trim();
        if (raw !== '[]' && raw !== '[""]' && raw !== '[ ]' && raw !== 'null') {
          if (raw.includes(':')) {
            raw.split(';').forEach(part => {
              const tokens = part.split(':');
              if (tokens.length >= 2 && tokens[1]) {
                list.push({ id: tokens[0], name: tokens[1] });
              }
            });
          } else {
            raw.split(',').forEach(name => {
              if (name.trim()) list.push({ id: null, name: name.trim() });
            });
          }
        }
      } else if (item.authorName) {
        list.push({ id: item.authorId || null, name: String(item.authorName).trim() });
      } else if (item.author) {
        if (typeof item.author === 'object') {
          list.push({ id: item.author.id || null, name: item.author.name || '' });
        } else if (typeof item.author === 'string' && item.author.trim()) {
          item.author.split(',').forEach(name => {
            if (name.trim()) list.push({ id: null, name: name.trim() });
          });
        }
      }
      return list.filter(a => a.name);
    },
    getTranslatorsStr(item) {
      if (!item) return '';
      const names = [];
      if (Array.isArray(item.translators)) {
        item.translators.forEach(t => {
          if (typeof t === 'object' && t !== null) {
            const name = t.name || t.translatorName || '';
            if (name) names.push(String(name).trim());
          } else if (typeof t === 'string' && t.trim()) {
            names.push(t.trim());
          }
        });
      }
      const raw = (item.translatorsStr || '').trim();
      if (names.length === 0 && raw && raw !== '[]' && raw !== '[""]' && raw !== '[ ]' && raw !== 'null') {
        if (raw.includes(':')) {
          raw.split(';').forEach(part => {
            const p = String(part).trim();
            if (!p) return;
            const name = p.includes(':') ? p.substring(p.indexOf(':') + 1).trim() : p;
            if (name) names.push(name);
          });
        } else {
          raw.split(',').forEach(name => {
            if (name.trim()) names.push(name.trim());
          });
        }
      }
      return names.join(', ');
    },
    getCategoriesList(item) {
      if (!item) return [];
      const list = [];
      if (Array.isArray(item.categoryList) && item.categoryList.length > 0) {
        item.categoryList.forEach(c => {
          if (typeof c === 'object' && c !== null) {
            const name = c.name || c.categoryName || '';
            if (name) list.push({ id: c.id || null, name });
          } else if (typeof c === 'string' && c.trim()) {
            list.push({ id: null, name: c.trim() });
          }
        });
      } else if (Array.isArray(item.categories) && item.categories.length > 0) {
        item.categories.forEach(c => {
          if (typeof c === 'object' && c !== null) {
            const name = c.name || c.categoryName || c.title || '';
            if (name) list.push({ id: c.id || null, name });
          } else if (typeof c === 'string' && c.trim()) {
            list.push({ id: null, name: c.trim() });
          }
        });
      } else if (Array.isArray(item.categoryNames) && item.categoryNames.length > 0) {
        item.categoryNames.forEach(name => {
          if (typeof name === 'string' && name.trim()) list.push({ id: null, name: name.trim() });
        });
      } else if (item.categoriesStr && typeof item.categoriesStr === 'string' && item.categoriesStr.trim()) {
        const raw = item.categoriesStr.trim();
        if (raw !== '[]' && raw !== '[""]' && raw !== '[ ]' && raw !== 'null') {
          if (raw.includes(':')) {
            raw.split(';').forEach(part => {
              const tokens = part.split(':');
              if (tokens.length >= 2 && tokens[1]) {
                list.push({ id: tokens[0], name: tokens[1] });
              }
            });
          } else {
            raw.split(',').forEach(name => {
              if (name.trim()) list.push({ id: null, name: name.trim() });
            });
          }
        }
      } else if (item.categoryName) {
        list.push({ id: item.categoryId || null, name: String(item.categoryName).trim() });
      }
      return list.filter(c => c.name);
    },
    async goSearchAuthor(authorObj) {
      if (!authorObj) return;
      const authorId = typeof authorObj === 'object' ? (authorObj.id || authorObj.authorId) : null;
      const authorName = typeof authorObj === 'object' ? (authorObj.name || authorObj.authorName || '') : String(authorObj);
      const cleanName = authorName ? authorName.trim() : '';

      if (authorId) {
        this.$router.push({
          path: `/author/${authorId}`,
          state: { authorName: cleanName }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') console.error(err);
        });
        return;
      }

      if (!cleanName) return;

      try {
        const res = await axios.get('author/v1/search', {
          params: { key: cleanName, page: 0 },
          _skip403Redirect: true
        });
        let list = [];
        if (res.data && res.data.status === '200' && res.data.result) {
          if (Array.isArray(res.data.result.content)) {
            list = res.data.result.content;
          } else if (Array.isArray(res.data.result)) {
            list = res.data.result;
          }
        }
        if (list.length > 0) {
          const match = list.find(a => a.name && a.name.trim().toLowerCase() === cleanName.toLowerCase()) || list[0];
          if (match && match.id) {
            this.$router.push({
              path: `/author/${match.id}`,
              state: { authorName: match.name || cleanName }
            }).catch(err => {
              if (err.name !== 'NavigationDuplicated') console.error(err);
            });
            return;
          }
        }
      } catch (e) {
        console.error('Error finding author by name:', e);
      }

      this.goSearch(cleanName);
    },
    async goSearchCategory(catObj) {
      if (!catObj) return;
      const catId = typeof catObj === 'object' ? (catObj.id || catObj.categoryId) : null;
      const catName = typeof catObj === 'object' ? (catObj.name || catObj.categoryName || '') : String(catObj);
      const cleanName = catName ? catName.trim() : '';

      if (catId) {
        this.$router.push({
          path: `/category/${catId}`,
          state: { categoryName: cleanName }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') console.error(err);
        });
        return;
      }

      if (!cleanName) return;

      try {
        const res = await axios.get('category/v1/get-all', { _skip403Redirect: true });
        if (res.data && res.data.status === '200' && Array.isArray(res.data.result)) {
          const categories = res.data.result;
          const match = categories.find(c => c.name && c.name.trim().toLowerCase() === cleanName.toLowerCase()) ||
                        categories.find(c => c.name && c.name.trim().toLowerCase().includes(cleanName.toLowerCase()));
          if (match && match.id) {
            this.$router.push({
              path: `/category/${match.id}`,
              state: { categoryName: match.name || cleanName }
            }).catch(err => {
              if (err.name !== 'NavigationDuplicated') console.error(err);
            });
            return;
          }
        }
      } catch (e) {
        console.error('Error finding category by name:', e);
      }

      this.goSearch(cleanName);
    },
    handleControlBarScroll() {
      const card = this.$el ? this.$el.querySelector('.top-control-bar-card') : null;
      if (!card) return;
      const rect = card.getBoundingClientRect();
      const targetTop = this.isMobile ? 56 : 64;
      this.isControlBarStuck = rect.top <= targetTop + 2;
    },
    fetchUserBookNotes(page = 0, isBackground = false) {
      if (!isBackground) {
        this.isLoading = true;
      }

      const params = {
        page: page,
        size: 12
      };

      if (!this.selectedStatuses.includes("all") && this.selectedStatuses.length > 0) {
        params.statuses = this.selectedStatuses.join(",");
      }
      if (!this.selectedOwnerships.includes("all") && this.selectedOwnerships.length > 0) {
        params.ownerships = this.selectedOwnerships.join(",");
      }
      if (this.searchKeyword) {
        params.keyword = this.searchKeyword;
      }
      params.sortBy = this.sortBy;

      axios
        .get("book-note/my-bookcase-search", { params })
        .then(response => {
          const data = response.data;
          if (data.status === "200" && data.result) {
            this.bookNotes = (data.result.content || []).map(item => {
              if (item.cover && !(item.cover.startsWith('data:') || item.cover.startsWith('http'))) {
                item.cover = 'data:image/png;base64,' + item.cover;
              }
              return item;
            });
            this.currentPage = data.result.currentPage || 0;
            this.totalPages = data.result.totalPages || 0;
            this.totalElements = data.result.totalElements || 0;
            this.stats = data.result.stats || null;
          } else {
            this.bookNotes = [];
            this.currentPage = 0;
            this.totalPages = 0;
            this.totalElements = 0;
            this.stats = null;
          }
        })
        .catch(error => {
          console.error("Error fetching user book notes:", error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    goToPage(page) {
      if (page === '...' || page < 0 || page >= this.totalPages) {
        return;
      }
      this.fetchUserBookNotes(page);
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    },

    setActiveTab(tabValue) {
      this.activeTab = tabValue;
    },

    onMouseDown(e) {
      const container = this.$refs.tabsContainer;
      if (!container) return;
      this.isDraggingTabs = true;
      this.hasDraggedFar = false;
      this.dragStartX = e.pageX - container.offsetLeft;
      this.dragScrollLeft = container.scrollLeft;
    },

    onMouseLeave() {
      this.isDraggingTabs = false;
    },

    onMouseUp() {
      this.isDraggingTabs = false;
    },

    onMouseMove(e) {
      if (!this.isDraggingTabs) return;
      const container = this.$refs.tabsContainer;
      if (!container) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - this.dragStartX) * 1.5;
      if (Math.abs(walk) > 6) {
        this.hasDraggedFar = true;
      }
      container.scrollLeft = this.dragScrollLeft - walk;
    },

    onTabClick(tabValue, e) {
      if (this.hasDraggedFar) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      this.setActiveTab(tabValue);
    },

    isStatusSelected(val) {
      if (val === "all") {
        return this.selectedStatuses.includes("all") || this.selectedStatuses.length === 0;
      }
      const num = parseInt(val, 10);
      return this.selectedStatuses.includes(num);
    },
    toggleStatusFilter(val) {
      if (val === "all") {
        this.selectedStatuses = ["all"];
        this.fetchUserBookNotes(0, true);
        return;
      }
      const num = parseInt(val, 10);
      let current = this.selectedStatuses.filter(s => s !== "all").map(s => parseInt(s, 10));
      if (current.includes(num)) {
        current = current.filter(s => s !== num);
      } else {
        current.push(num);
      }
      const allSpecificStatuses = [1, 2, 3, 4];
      if (current.length === 0 || current.length >= allSpecificStatuses.length) {
        this.selectedStatuses = ["all"];
      } else {
        this.selectedStatuses = current;
      }
      this.fetchUserBookNotes(0, true);
    },
    isOwnSelected(val) {
      if (val === "all") {
        return this.selectedOwnerships.includes("all") || this.selectedOwnerships.length === 0;
      }
      const num = parseInt(val, 10);
      return this.selectedOwnerships.includes(num);
    },
    toggleOwnFilter(val) {
      if (val === "all") {
        this.selectedOwnerships = ["all"];
        this.fetchUserBookNotes(0, true);
        return;
      }
      const num = parseInt(val, 10);
      let current = this.selectedOwnerships.filter(s => s !== "all").map(s => parseInt(s, 10));
      if (current.includes(num)) {
        current = current.filter(s => s !== num);
      } else {
        current.push(num);
      }
      const allSpecificOwnerships = [0, 1, 2];
      if (current.length === 0 || current.length >= allSpecificOwnerships.length) {
        this.selectedOwnerships = ["all"];
      } else {
        this.selectedOwnerships = current;
      }
      this.fetchUserBookNotes(0, true);
    },

    selectSort(value) {
      this.sortBy = value;
      this.showSortDropdown = false;
      this.fetchUserBookNotes(0, true);
    },

    selectOwnFilter(value) {
      this.ownFilter = value;
      this.showOwnDropdown = false;
    },

    getSortIcon(value) {
      const opt = this.sortOptions.find(o => o.value === value);
      return opt ? opt.icon : "fa-solid fa-sort";
    },

    getOwnStatusLabel(status) {
      switch (status) {
        case 1: return "Sách giấy";
        case 2: return "Ebook";
        case 0: default: return "Chưa sở hữu";
      }
    },

    getOwnStatusIcon(status) {
      switch (status) {
        case 1: return "fa-solid fa-book";
        case 2: return "fa-solid fa-tablet-screen-button";
        case 0: default: return "fa-solid fa-xmark";
      }
    },

    getOwnStatusBadgeClass(status) {
      switch (status) {
        case 1: return "badge-own-paper";
        case 2: return "badge-own-ebook";
        case 0: default: return "badge-own-none";
      }
    },

    getStatusLabel(status) {
      switch (status) {
        case 0: return "Chưa đọc";
        case 1: return "Muốn đọc";
        case 2: return "Đang đọc";
        case 3: return "Đã đọc xong";
        case 4: return "Tạm ngưng";
        default: return "Chưa xác định";
      }
    },

    getStatusIcon(status) {
      switch (status) {
        case 0: return "fa-solid fa-xmark";
        case 1: return "fa-solid fa-bookmark";
        case 2: return "fa-solid fa-book-open";
        case 3: return "fa-solid fa-circle-check";
        case 4: return "fa-solid fa-circle-pause";
        default: return "fa-solid fa-book";
      }
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case 0: return "badge-status-unread";
        case 1: return "badge-status-want";
        case 2: return "badge-status-reading";
        case 3: return "badge-status-completed";
        case 4: return "badge-status-paused";
        default: return "badge-status-unread";
      }
    },

    getProgressBarClass(status) {
      switch (status) {
        case 2: return "bg-success-gradient";
        case 3: return "bg-purple-gradient";
        case 4: return "bg-warning-gradient";
        default: return "bg-primary-gradient";
      }
    },

    getProgressTextClass(status) {
      switch (status) {
        case 3: return "text-completed";
        default: return "";
      }
    },

    calculateVisualPercent(item) {
      if (item.progressStatus === 3) return 100;
      if (item.progressStatus === 0 || item.progressStatus === 1) return 0;
      if (item.type) {
        return Math.min(100, Math.max(0, item.percent || 0));
      }
      if (item.totalPage > 0 && item.currentPage >= 0) {
        return Math.min(100, Math.max(0, Math.round((item.currentPage / item.totalPage) * 100)));
      }
      return item.percent || 0;
    },

    formatProgressText(item) {
      if (item.progressStatus === 3) return "100% (Đã đọc xong)";
      if (item.progressStatus === 0) return "0% (Chưa đọc)";
      if (item.progressStatus === 1) return "0% (Muốn đọc)";

      if (item.type) {
        return `${(item.percent || 0).toFixed(0)}%`;
      }
      if (item.totalPage > 0) {
        const pct = Math.round(((item.currentPage || 0) / item.totalPage) * 100);
        return `Trang ${item.currentPage || 0}/${item.totalPage} (${pct}%)`;
      }
      return `${(item.percent || 0).toFixed(0)}%`;
    },

    isValidNote(note) {
      if (!note) return false;
      const trimmed = note.trim();
      return (
        trimmed !== "" &&
        trimmed !== "." &&
        trimmed !== "-" &&
        !trimmed.startsWith("Cập nhật trạng thái:")
      );
    },

    formatDateTime(timeStr) {
      if (!timeStr) return "";
      const str = String(timeStr).trim();
      if (/^\d{14}$/.test(str)) {
        const yyyy = str.substring(0, 4);
        const mm = str.substring(4, 6);
        const dd = str.substring(6, 8);
        const hh = str.substring(8, 10);
        const min = str.substring(10, 12);
        const ss = str.substring(12, 14);
        return `${hh}:${min}:${ss} ${dd}/${mm}/${yyyy}`;
      }
      if (/^\d{12}$/.test(str)) {
        const yyyy = str.substring(0, 4);
        const mm = str.substring(4, 6);
        const dd = str.substring(6, 8);
        const hh = str.substring(8, 10);
        const min = str.substring(10, 12);
        return `${hh}:${min}:00 ${dd}/${mm}/${yyyy}`;
      }
      if (/^\d{2}:\d{2}\s+\d{2}\/\d{2}\/\d{4}$/.test(str)) {
        const parts = str.split(/\s+/);
        return `${parts[0]}:00 ${parts[1]}`;
      }
      return str;
    },

    onImageError(e) {
      e.target.src = "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?auto=format&fit=crop&w=400&q=80";
    },

    isLongNote(note) {
      return this.isValidNote(note) && note.trim().length > 50;
    },

    isLongNoteGrid(note) {
      return this.isValidNote(note) && note.trim().length > 15;
    },

    openNoteView(item) {
      this.viewNoteItem = item;
      this.showNoteViewModal = true;
    },

    closeNoteView() {
      this.showNoteViewModal = false;
      this.viewNoteItem = null;
    },

    toggleNoteExpand(bookId) {
      this.expandedNotes[bookId] = !this.expandedNotes[bookId];
    },

    isNoteExpanded(bookId) {
      return !!this.expandedNotes[bookId];
    },

    // Edit Modal Logic
    openEditModal(item) {
      this.activeEditItem = item;
      const isDefaultNote = !item.note || item.note === "." || item.note === "-" || item.note.startsWith("Cập nhật trạng thái:");
      
      const parsedStatus = item.progressStatus !== undefined && item.progressStatus !== null ? Number(item.progressStatus) : 2;
      const parsedOwn = item.ownStatus !== undefined && item.ownStatus !== null ? Number(item.ownStatus) : 0;

      this.modalForm = {
        bookId: item.bookId,
        progressStatus: isNaN(parsedStatus) ? 2 : parsedStatus,
        type: Boolean(item.type),
        currentPage: Number(item.currentPage) || 0,
        totalPage: Number(item.totalPage) || 0,
        percent: parseFloat(item.percent) || 0,
        note: isDefaultNote ? "" : item.note,
        ownStatus: isNaN(parsedOwn) ? 0 : parsedOwn
      };
      this.showNoteTextarea = !isDefaultNote;
      this.originalBookNote = JSON.parse(JSON.stringify(this.modalForm));
      this.showModal = true;
    },

    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },

    toggleReadingStatus() {
      if (!this.isMobile) return;
      this.collapseReadingStatus = !this.collapseReadingStatus;
    },

    toggleOwnership() {
      if (!this.isMobile) return;
      this.collapseOwnership = !this.collapseOwnership;
    },

    selectProgressStatus(value) {
      this.modalForm.progressStatus = Number(value);
    },

    calculatePercent() {
      if (!this.modalForm.type) {
        if (this.modalForm.totalPage > 0) {
          const pct = (this.modalForm.currentPage / this.modalForm.totalPage) * 100;
          this.modalForm.percent = Math.round(pct * 10) / 10;
        } else {
          this.modalForm.percent = 0;
        }
      }
    },

    calculatePageFromPercent() {
      if (this.modalForm.type && this.modalForm.totalPage > 0) {
        const pct = parseFloat(this.modalForm.percent) || 0;
        const page = Math.round((pct / 100) * this.modalForm.totalPage);
        this.modalForm.currentPage = Math.min(this.modalForm.totalPage, Math.max(0, page));
      }
    },

    onPercentInput(e) {
      if (e && e.target) {
        let val = parseFloat(e.target.value);
        if (isNaN(val)) val = 0;
        if (val > 100) val = 100;
        if (val < 0) val = 0;
        this.modalForm.percent = val;
      }
      this.calculatePageFromPercent();
    },

    setNoteType(isPercent) {
      this.modalForm.type = isPercent;
      if (isPercent) {
        this.calculatePercent();
      } else {
        if (this.modalForm.totalPage > 0) {
          this.calculatePageFromPercent();
        }
      }
    },

    openNoteTextarea() {
      this.showNoteTextarea = true;
      if (!this.modalForm.note && this.activeEditItem?.note) {
        const isDefault = !this.activeEditItem.note || this.activeEditItem.note === '.' || this.activeEditItem.note === '-' || this.activeEditItem.note.startsWith('Cập nhật trạng thái: ');
        if (!isDefault) {
          this.modalForm.note = this.activeEditItem.note;
        }
      }
    },

    hideNoteTextarea() {
      this.showNoteTextarea = false;
      const originalNoteText = this.activeEditItem?.note || '';
      const isDefault = !originalNoteText || originalNoteText === '.' || originalNoteText === '-' || originalNoteText.startsWith('Cập nhật trạng thái: ');
      if (!isDefault) {
        this.modalForm.note = originalNoteText;
      } else {
        this.modalForm.note = '';
      }
    },

    getModalVisualPercent() {
      const p = parseFloat(this.modalForm.percent);
      if (!isNaN(p) && this.modalForm.type) {
        return Math.min(100, Math.max(0, Math.round(p * 10) / 10));
      }
      if (this.modalForm.progressStatus === 3) return 100;
      if (this.modalForm.progressStatus === 0 || this.modalForm.progressStatus === 1) return 0;
      if (this.modalForm.totalPage > 0) {
        const pct = (this.modalForm.currentPage / this.modalForm.totalPage) * 100;
        return Math.min(100, Math.max(0, Math.round(pct * 10) / 10));
      }
      return 0;
    },

    closeModal() {
      this.showModal = false;
      if (this.originalBookNote) {
        this.modalForm = JSON.parse(JSON.stringify(this.originalBookNote));
        const noteText = this.modalForm.note || '';
        const isDefaultNote = !noteText || noteText === '.' || noteText === '-' || noteText.startsWith('Cập nhật trạng thái: ');
        this.showNoteTextarea = !isDefaultNote;
      }
      this.activeEditItem = null;
      this.originalBookNote = null;
    },

    closeFilterModal() {
      this.showFilterModal = false;
    },

    handleClickOutside(e) {
      const sortEl = this.$el?.querySelector(".sort-dropdown-wrapper");
      if (sortEl && !sortEl.contains(e.target)) {
        this.showSortDropdown = false;
      }
      const ownEl = this.$el?.querySelector(".own-filter-dropdown-wrapper");
      if (ownEl && !ownEl.contains(e.target)) {
        this.showOwnDropdown = false;
      }
    },

    saveBookNote() {
      if (this.noteValidationError) {
        const toast = useToast();
        toast.error(this.noteValidationError);
        return;
      }

      this.isSaving = true;
      const toast = useToast();

      let noteToSend = this.modalForm.note ? this.modalForm.note.trim() : "";
      if (!noteToSend) {
        noteToSend = ".";
      }

      let calcPage = this.modalForm.currentPage;
      if (this.modalForm.type && this.modalForm.totalPage > 0) {
        const pct = parseFloat(this.modalForm.percent) || 0;
        calcPage = Math.min(this.modalForm.totalPage, Math.max(0, Math.round((pct / 100) * this.modalForm.totalPage)));
      }

      const status = this.modalForm.progressStatus;
      const isNoProgress = (status === 0 || status === 1);
      const isCompleted = (status === 3);

      const payload = {
        bookId: this.modalForm.bookId,
        note: noteToSend,
        currentPage: isNoProgress ? 0 : (isCompleted ? (this.modalForm.totalPage > 0 ? this.modalForm.totalPage : 0) : calcPage),
        totalPage: isNoProgress ? 0 : (this.modalForm.totalPage || 0),
        percent: isNoProgress ? 0.0 : (isCompleted ? 100.0 : (this.modalForm.type ? parseFloat(this.modalForm.percent) : this.visualPercent)),
        type: this.modalForm.type,
        progressStatus: this.modalForm.progressStatus,
        ownStatus: this.modalForm.ownStatus
      };

      const isStatusOrOwnChanged = !this.originalBookNote ||
        Number(this.originalBookNote.progressStatus) !== Number(payload.progressStatus) ||
        Number(this.originalBookNote.ownStatus) !== Number(payload.ownStatus);

      axios
        .post("book-note/save", payload)
        .then(response => {
          const data = response.data;
          if (data.status === "200") {
            toast.success(data.message || "Cập nhật tiến độ đọc sách thành công.");
            if (isStatusOrOwnChanged) {
              updateTrendingCacheIfBookExists(this.modalForm.bookId);
            }
            this.closeModal();
            this.fetchUserBookNotes(this.currentPage, true);
          } else {
            toast.error(data.message || "Đã xảy ra lỗi khi lưu tiến độ.");
          }
        })
        .catch(error => {
          console.error("Error saving book note:", error);
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    confirmDeleteBookcase(item) {
      this.deleteConfirmModal = {
        show: true,
        bookId: item.bookId,
        bookName: item.bookName,
        isDeleting: false
      };
    },
    closeDeleteModal() {
      this.deleteConfirmModal = {
        show: false,
        bookId: null,
        bookName: null,
        isDeleting: false
      };
    },
    executeDelete() {
      if (!this.deleteConfirmModal.bookId) return;
      this.deleteConfirmModal.isDeleting = true;
      const toast = useToast();
      axios
        .post(`book-note/remove/${this.deleteConfirmModal.bookId}`)
        .then(response => {
          const data = response.data;
          if (data.status === "200") {
            toast.success("Đã xóa sách khỏi tủ sách thành công.");
            updateTrendingCacheIfBookExists(this.deleteConfirmModal.bookId);
            this.closeDeleteModal();
            this.fetchUserBookNotes(0, true);
          } else {
            toast.error(data.message || "Đã xảy ra lỗi khi xóa sách.");
            this.deleteConfirmModal.isDeleting = false;
          }
        })
        .catch(error => {
          console.error("Error removing book from bookcase:", error);
          this.deleteConfirmModal.isDeleting = false;
        });
    }
  }
};
